// Ticket-Steuerung der Pille als Widgets (Handoff Pille §6) – dieselben Werte und Callbacks wie die Ticket-Liste
// (die bleibt als Seitenleiste der Liste), hier nur als Daten für die generische Widget-Komponente.

import { ArrowCounterClockwise, Briefcase, Buildings, ChartBar, Flag, FlowArrow, ListChecks, Plus, Tag, Ticket, Tray, User, UserCircle } from '@phosphor-icons/react';
import type { PillenSteuerung, SteuerungsEintrag } from '../../../components/island/widgets/typen';
import type { ElementType } from 'react';
import type { QuickFilter } from './TicketListe';

export interface KorbGruppe {
  titel: string;
  items: { key: QuickFilter; label: string; count: number; Icon: ElementType }[];
}

/** Werte und Callbacks der Ticket-Steuerung (Liste → Pille). */
export interface TicketSteuerungProps {
  searchTerm: string;
  setSearchTerm: (v: string) => void;
  korbGruppen: KorbGruppe[];
  quickFilter: QuickFilter;
  setQuickFilter: (k: QuickFilter) => void;
  statusConfig: Record<string, { label: string }>;
  statusCounts: Record<string, number>;
  filterStatus: string;
  setFilterStatus: (v: string) => void;
  prioConfig: Record<string, { label: string }>;
  prioCounts: Record<string, number>;
  filterPrio: string;
  setFilterPrio: (v: string) => void;
  hauptKategorien: { id: string; name: string }[];
  filterKategorie: string;
  setFilterKategorie: (v: string) => void;
  teamMembers: { id: string; full_name: string }[];
  teams?: { id: string; name: string }[];
  filterZustaendig: string;
  setFilterZustaendig: (v: string) => void;
  objekte?: { id: string; bezeichnung: string | null }[];
  filterObjekt?: string;
  setFilterObjekt?: (v: string) => void;
  stats: { label: string; value: number }[];
  hasActiveFilters: boolean;
  resetFilters: () => void;
  onNewTicket: () => void;
  onChecklistenVorlagen: () => void;
  onVorgangsVorlagen: () => void;
}

const KORB_ICON = [Tray, User, Briefcase] as const;
const KORB_FARBE = ['blau', 'indigo', 'grau'] as const;

export function ticketPillenSteuerung(p: TicketSteuerungProps): PillenSteuerung {
  const mitZahl = (label: string, n: number | undefined) => `${label} · ${n ?? 0}`;
  const eintraege: SteuerungsEintrag[] = [
    { id: 'neu', typ: 'aktion', farbe: 'gold', icon: Plus, kicker: 'Anlegen', titel: 'Neues Ticket', unter: 'Schaden oder Anfrage',
      tooltip: 'Neues Ticket anlegen', onClick: p.onNewTicket },
    { id: 'checklisten', typ: 'aktion', farbe: 'tuerkis', icon: ListChecks, kicker: 'Extras', titel: 'Checklisten-Vorlagen', unter: 'Vorlagen verwalten',
      tooltip: 'Checklisten-Vorlagen für Tickets verwalten', onClick: p.onChecklistenVorlagen },
    { id: 'vorgaenge', typ: 'aktion', farbe: 'violett', icon: FlowArrow, kicker: 'Extras', titel: 'Vorgangs-Vorlagen', unter: 'Vorgangsarten verwalten',
      tooltip: 'Vorgangsarten (Kündigung, Schaden, Zählerstand …) verwalten', onClick: p.onVorgangsVorlagen },
    { abschnitt: 'Ansichten' },
    ...p.korbGruppen.map((g, i): SteuerungsEintrag => ({
      id: `korb-${g.titel}`, typ: 'kennzahl', farbe: KORB_FARBE[i] ?? 'blau', icon: KORB_ICON[i] ?? Ticket, kicker: g.titel,
      werte: g.items.map(k => ({ label: k.label, zahl: k.count, aktiv: p.quickFilter === k.key, onClick: () => p.setQuickFilter(k.key) })),
    })),
    { abschnitt: 'Filter' },
    { id: 'status', typ: 'auswahl', farbe: 'violett', icon: Ticket, kicker: 'Status', standard: 'alle', wert: p.filterStatus, onChange: p.setFilterStatus,
      tooltip: 'Tickets nach Status filtern',
      optionen: [{ wert: 'alle', label: 'Alle Status' }, ...Object.entries(p.statusConfig).map(([k, c]) => ({ wert: k, label: mitZahl(c.label, p.statusCounts[k]) }))] },
    { id: 'prio', typ: 'auswahl', farbe: 'orange', icon: Flag, kicker: 'Priorität', standard: 'alle', wert: p.filterPrio, onChange: p.setFilterPrio,
      tooltip: 'Tickets nach Priorität filtern',
      optionen: [{ wert: 'alle', label: 'Alle Prioritäten' }, ...Object.entries(p.prioConfig).map(([k, c]) => ({ wert: k, label: mitZahl(c.label, p.prioCounts[k]) }))] },
    { id: 'kategorie', typ: 'auswahl', farbe: 'tuerkis', icon: Tag, kicker: 'Kategorie', standard: 'alle', wert: p.filterKategorie, onChange: p.setFilterKategorie,
      tooltip: 'Tickets nach Kategorie filtern',
      optionen: [{ wert: 'alle', label: 'Alle Kategorien' }, ...p.hauptKategorien.map(k => ({ wert: k.id, label: k.name }))] },
    { id: 'zustaendig', typ: 'auswahl', farbe: 'indigo', icon: UserCircle, kicker: 'Zuständig', standard: 'alle', wert: p.filterZustaendig, onChange: p.setFilterZustaendig,
      tooltip: 'Tickets nach zuständiger Person oder Team filtern',
      optionen: [
        { wert: 'alle', label: 'Alle Zuständige' },
        { wert: 'global', label: 'Ohne Zuständige' },
        ...p.teamMembers.map(m => ({ wert: m.id, label: m.full_name || 'Unbekannt', gruppe: 'Mitarbeiter' })),
        ...(p.teams ?? []).map(t => ({ wert: `t:${t.id}`, label: t.name, gruppe: 'Teams' })),
      ] },
  ];
  if (p.setFilterObjekt) {
    eintraege.push({ id: 'objekt', typ: 'auswahl', farbe: 'blau', icon: Buildings, kicker: 'Objekt', standard: 'alle', wert: p.filterObjekt ?? 'alle',
      onChange: p.setFilterObjekt, tooltip: 'Tickets nach Objekt filtern',
      optionen: [{ wert: 'alle', label: 'Alle Objekte' }, ...(p.objekte ?? []).map(o => ({ wert: o.id, label: o.bezeichnung || 'Ohne Bezeichnung' }))] });
  }
  if (p.hasActiveFilters) {
    eintraege.push({ id: 'reset', typ: 'aktion', farbe: 'grau', icon: ArrowCounterClockwise, kicker: 'Zurücksetzen', titel: 'Filter zurücksetzen',
      unter: 'Alle Filter auf Standard', onClick: p.resetFilters });
  }
  eintraege.push({ id: 'zahlen', typ: 'kennzahl', farbe: 'blau', icon: ChartBar, kicker: 'Übersicht', werte: p.stats.map(s => ({ label: s.label, zahl: s.value })) });
  return { titel: 'Tickets', suche: { platzhalter: 'Tickets suchen…', wert: p.searchTerm, onChange: p.setSearchTerm }, eintraege };
}
